import { createStore } from '@shared/utils/state/state-factory'
import { queryClient } from 'v2/configs/rq-setup'
import { getCallConversation } from 'v2/pages/conversations/utils'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RActiveConversation } from 'v2/types/api/conversations'
import { type CallDetails, CallDirection, type PhoneState, type PhoneStore } from 'v2/types/call'
import { useTabStore } from './tab'

const initialState: PhoneState = {
  activeCall: null,
  isCallActive: false,
  isMuted: false,
  isOnHold: false,
  isRegistered: false,
  callConversation: {},
  participants: [], // Array of participants, other than the customer
  callPickedup: false,
  isRecordingPaused: false,
  isSecondaryParticipant: false,
  monitoringState: null,
  isMonitorInitiator: false,
}

export const usePhoneStore = createStore<PhoneStore>(
  'phone',
  (set) => ({
    ...initialState,
    setActiveCall: (call: Partial<CallDetails> | null) =>
      set((state) => ({ activeCall: call ? { ...state.activeCall, ...call } : null })),
    setCallActive: (status: boolean) => set({ isCallActive: status }),
    setMuted: (status: boolean) => set({ isMuted: status }),
    setOnHold: (status: boolean) => set({ isOnHold: status }),
    reset: (clearConnectionState = false) =>
      set((state) => {
        if (clearConnectionState) useTabStore.getState().setIsPhoneConnected(false)

        return {
          ...initialState,
          isRegistered: clearConnectionState ? false : state.isRegistered,
        }
      }),
    setRegistered: (status: boolean) => set({ isRegistered: status }),
    setCallConversation: (activeConversations?: RActiveConversation[]) => {
      const conversations =
        activeConversations !== undefined
          ? activeConversations
          : queryClient.getQueryData<RActiveConversation[]>(conversationQueryKeys().active)

      if (!conversations) return set({ callConversation: {}, callPickedup: false })
      const { callChildConversation, parentConversation } = getCallConversation(conversations)

      if (!parentConversation) return set({ callConversation: {}, callPickedup: false })

      set((state) => ({
        callConversation: {
          callChildConversation,
          parentConversation,
        },
        callPickedup:
          state.activeCall?.direction === CallDirection.OUTBOUND
            ? state.callPickedup
            : !!callChildConversation?.start_at,
      }))
    },
    setCallPickedup: (status) => set({ callPickedup: status }),
    setIsRecordingPaused: (isPaused) => set({ isRecordingPaused: isPaused }),

    // Participants
    addParticipant: (participant) =>
      set((state) => {
        if (!state.activeCall) return // Only add participants to active calls
        if (!state.participants) state.participants = []
        state.participants?.push(participant)
      }),
    updateParticipant: (member, participant) =>
      set((state) => {
        if (!state.participants) return
        const index = state.participants.findIndex((p) => p.member === member)
        if (index === -1) return
        state.participants[index] = { ...state.participants[index], ...participant }
      }),
    // Clear all participants except monitor, because agent cannot remove the supervisor from the call
    clearParticipants: () =>
      set((state) => ({
        participants: state.participants.filter((p) => p.participantType === 'monitor'),
      })),
    removeParticipant: (member) =>
      set((state) => ({
        participants: state.participants?.filter((p) => p.member !== member),
      })),
    setIsSecondaryParticipant: (status) => set({ isSecondaryParticipant: status }),
    setMonitoringState: (state) => set({ monitoringState: state }),
    setIsMonitorInitiator: (status) => set({ isMonitorInitiator: status }),
  }),
  {
    shared: true,
  },
)
